<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import {
  checkFileSizeLimit,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { mediaHeader } from 'custom/helpers/channelTemplatePresets';

const props = defineProps({
  template: { type: Object, required: true },
  disabled: { type: Boolean, default: false },
  filename: { type: String, default: '' },
  source: { type: String, default: '' },
});

const emit = defineEmits(['select']);
const { t } = useI18n();
const store = useStore();

const sourceLabels = computed(() => ({
  channel: t('CHANNEL_TEMPLATE_PRESETS.MEDIA_SOURCES.channel'),
  meta: t('CHANNEL_TEMPLATE_PRESETS.MEDIA_SOURCES.meta'),
  current: t('CHANNEL_TEMPLATE_PRESETS.MEDIA_SOURCES.current'),
}));
const sourceLabel = computed(() => sourceLabels.value[props.source] || '');

const compatibleWithHeader = file => {
  const format = mediaHeader(props.template)?.format;
  return (
    (format === 'IMAGE' && file.type.startsWith('image/')) ||
    (format === 'VIDEO' && file.type.startsWith('video/')) ||
    (format === 'DOCUMENT' && !file.type.match(/^(image|video|audio)\//))
  );
};

const selectFile = event => {
  const [file] = event.target.files || [];
  event.target.value = '';
  if (!file) return;

  const limit = resolveMaximumFileUploadSize(
    store.getters['globalConfig/get']?.maximumFileUploadSize
  );
  if (!checkFileSizeLimit(file, limit)) {
    useAlert(t('CHANNEL_TEMPLATE_PRESETS.FILE_TOO_LARGE', { limit }));
    return;
  }
  if (!compatibleWithHeader(file)) {
    useAlert(t('CHANNEL_TEMPLATE_PRESETS.FILE_TYPE_ERROR'));
    return;
  }
  emit('select', file);
};
</script>

<template>
  <div>
    <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
      {{ t('CHANNEL_TEMPLATE_PRESETS.MEDIA_UPLOAD_LABEL') }}
    </label>
    <input
      type="file"
      class="block w-full text-sm text-n-slate-12 file:mr-3 file:rounded-lg file:border-0 file:bg-n-alpha-2 file:px-3 file:py-2 file:text-sm file:text-n-slate-12"
      :disabled="disabled"
      @change="selectFile"
    />
    <div
      v-if="filename || sourceLabel"
      class="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-n-slate-11"
    >
      <span v-if="filename" class="font-medium text-n-slate-12">
        {{ filename }}
      </span>
      <span v-if="sourceLabel">
        {{
          t('CHANNEL_TEMPLATE_PRESETS.MEDIA_SOURCE', { source: sourceLabel })
        }}
      </span>
    </div>
  </div>
</template>
